import React, { useState } from "react";
import { useNavigate } from 'react-router-dom';
import { authActions } from '../redux/actions';
import Helper1, { notify } from '../utility/Helper1';
import { useSelector, useDispatch } from 'react-redux';
import { WebLink, SiteWide, Images } from "./customComponents";
import { allRemoteSettingsSelect } from "../redux/selectors";

import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import EmojiEmotionsIcon from "@mui/icons-material/EmojiEmotions";
import MonetizationOnOutlinedIcon from "@mui/icons-material/MonetizationOnOutlined";

function Signin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [focused, setFocused] = useState(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const site_settings = useSelector(allRemoteSettingsSelect);

  const userLoginStyle = site_settings?.operational_settings?.userLoginStyle ?? 0;

  let loginLabel = "Username";
  let loginPlaceholder = "Enter your username";
  let inputType = "text";

  if (userLoginStyle === 1) {
    loginLabel = "Email Address";
    loginPlaceholder = "Enter your email address";
    inputType = "email";
  } else if (userLoginStyle === 2) {
    loginLabel = "Username or Email";
    loginPlaceholder = "Enter your username or email";
    inputType = "text";
  }

  const doLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    dispatch(authActions.onLogin("account/login", { username: email, password }, response => {
      if (response.code != 0) {
        if (response.code == 1) { navigate('/'); }
        else if (response.code == 2) { navigate('/verify-email'); }
      } else {
        notify(response.msg, response.code);
      }
      setLoading(false);
    }));
  };

  const inputStyle = (field) => ({
    width: "100%",
    borderRadius: 12,
    padding: "13px 14px 13px 42px",
    border: `1.5px solid ${focused === field ? "#2563eb" : "rgba(59,130,246,0.18)"}`,
    background: focused === field ? "#f0f7ff" : "#f8fafc",
    color: "#0f172a",
    fontSize: 14,
    fontFamily: "'DM Sans',sans-serif",
    outline: "none",
    boxSizing: "border-box",
    transition: "all 0.2s",
    boxShadow: focused === field ? "0 0 0 3px rgba(59,130,246,0.1)" : "none"
  });

  const features = [
    { icon: <ChatBubbleOutlineIcon style={{ fontSize: 20 }} />, text: "Chat with real people, in real time" },
    { icon: <PeopleAltIcon style={{ fontSize: 20 }} />, text: "Join a growing social community" },
    { icon: <EmojiEmotionsIcon style={{ fontSize: 20 }} />, text: "Express yourself freely & have fun" },
    { icon: <MonetizationOnOutlinedIcon style={{ fontSize: 20 }} />, text: "Earn rewards just by staying active" },
  ];

  return (
    <SiteWide>
      <div style={{
        minHeight: "100vh", display: "flex", alignItems: "stretch",
        background: "#f8fafc", fontFamily: "'DM Sans',sans-serif"
      }}>

        {/* ── Left panel (decorative) ── */}
        <div style={{
          flex: "0 0 42%", position: "relative", overflow: "hidden",
          background: "linear-gradient(145deg,#0f172a 0%,#1e3a8a 40%,#1d4ed8 75%,#2563eb 100%)",
          display: "flex", flexDirection: "column",
          padding: "48px 40px", boxSizing: "border-box"
        }}
          className="auth-left-panel"
        >
          {/* Ambient glow blobs */}
          <div style={{
            position: "absolute", top: -60, right: -60, width: 280, height: 280,
            borderRadius: "50%", background: "rgba(96,165,250,0.15)",
            filter: "blur(60px)"
          }} />
          <div style={{
            position: "absolute", bottom: 40, left: -40, width: 240, height: 240,
            borderRadius: "50%", background: "rgba(167,139,250,0.1)",
            filter: "blur(60px)"
          }} />

          {/* Logo */}
          <div style={{ position: "relative", zIndex: 1, marginBottom: "auto" }}>
            <Images
              height="44px"
              src={site_settings?.site_settings?.logo}
              alt="logo"
              style={{ filter: "brightness(0) invert(1)" }}
            />
          </div>

          {/* Hero text */}
          <div style={{ position: "relative", zIndex: 1, marginBottom: 48 }}>
            <h1 style={{
              color: "#ffffff", fontSize: 36, fontWeight: 800,
              fontFamily: "'Syne',sans-serif", lineHeight: 1.2,
              margin: "0 0 12px"
            }}>
              Welcome<br />back to<br />
              <span style={{ color: "#93c5fd" }}>the chat.</span>
            </h1>
            <p style={{ color: "rgba(219,234,254,0.65)", fontSize: 14, margin: 0, lineHeight: 1.6 }}>
              Sign in to connect with your community,<br />chat freely, and enjoy the experience.
            </p>
          </div>

          {/* Feature bullets */}
          <div style={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", gap: 14 }}>
            {features.map((f, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                  background: "rgba(255,255,255,0.1)", backdropFilter: "blur(8px)",
                  border: "1px solid rgba(255,255,255,0.12)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: "#93c5fd"
                }}>
                  {f.icon}
                </div>
                <span style={{ color: "rgba(219,234,254,0.75)", fontSize: 13, fontFamily: "'DM Sans',sans-serif" }}>
                  {f.text}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* ── Right panel (form) ── */}
        <div style={{
          flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
          padding: "40px 24px", overflowY: "auto"
        }}>
          <div style={{ width: "100%", maxWidth: 380 }}>

            {/* Mobile logo */}
            <div style={{ textAlign: "center", marginBottom: 32, display: "none" }} className="auth-mobile-logo">
              <Images height="40px" src={site_settings?.site_settings?.logo} alt="logo" />
            </div>

            {/* Heading */}
            <h2 style={{
              color: "#0f172a", fontSize: 26, fontWeight: 800,
              fontFamily: "'Syne',sans-serif", margin: "0 0 6px"
            }}>
              Sign in
            </h2>
            <p style={{ color: "#64748b", fontSize: 13, fontFamily: "'DM Sans',sans-serif", margin: "0 0 32px" }}>
              Don't have an account?{" "}
              <WebLink to="/signup" style={{ color: "#2563eb", fontWeight: 600, textDecoration: "none" }}>
                Create one free
              </WebLink>
            </p>

            <form onSubmit={doLogin} style={{ display: "flex", flexDirection: "column", gap: 18 }}>

              {/* Username/Email */}
              <div>
                <label style={{ color: "#334155", fontSize: 12, fontWeight: 600, display: "block", marginBottom: 6 }}>
                  {loginLabel}
                </label>
                <div style={{ position: "relative" }}>
                  <PersonOutlineIcon style={{
                    position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)",
                    color: focused === 'user' ? "#2563eb" : "#94a3b8", fontSize: 18,
                    transition: "color 0.2s"
                  }} />
                  <input
                    type={inputType}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onFocus={() => setFocused('user')}
                    onBlur={() => setFocused(null)}
                    placeholder={loginPlaceholder}
                    required
                    style={inputStyle('user')}
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                  <label style={{ color: "#334155", fontSize: 12, fontWeight: 600 }}>Password</label>
                  <WebLink to="/reset-password" style={{ color: "#2563eb", fontSize: 12, fontWeight: 600, textDecoration: "none" }}>
                    Forgot?
                  </WebLink>
                </div>
                <div style={{ position: "relative" }}>
                  <LockOutlinedIcon style={{
                    position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)",
                    color: focused === 'pass' ? "#2563eb" : "#94a3b8", fontSize: 18,
                    transition: "color 0.2s"
                  }} />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={() => setFocused('pass')}
                    onBlur={() => setFocused(null)}
                    placeholder="Enter your password"
                    required
                    style={{ ...inputStyle('pass'), paddingRight: 44 }}
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} style={{
                    position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)",
                    background: "none", border: "none", cursor: "pointer",
                    color: "#94a3b8", display: "flex", alignItems: "center", padding: 0
                  }}>
                    {showPassword ? <VisibilityOffIcon style={{ fontSize: 18 }} /> : <VisibilityIcon style={{ fontSize: 18 }} />}
                  </button>
                </div>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                style={{
                  width: "100%", borderRadius: 12, padding: "14px 0",
                  border: "none", marginTop: 4,
                  background: loading ? "rgba(37,99,235,0.4)" : "linear-gradient(135deg,#1d4ed8,#2563eb)",
                  color: "#fff", fontSize: 14, fontWeight: 700,
                  fontFamily: "'Syne',sans-serif",
                  cursor: loading ? "not-allowed" : "pointer",
                  boxShadow: loading ? "none" : "0 6px 20px rgba(37,99,235,0.35)",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  transition: "all 0.2s"
                }}
              >
                {loading ? (
                  <div style={{
                    width: 18, height: 18, border: "2.5px solid rgba(255,255,255,0.4)",
                    borderTopColor: "#fff", borderRadius: "50%", animation: "spin 0.8s linear infinite"
                  }} />
                ) : (
                  <>Sign In <ArrowForwardIcon style={{ fontSize: 16 }} /></>
                )}
              </button>
            </form>

          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @media (max-width: 640px) {
          .auth-left-panel { display: none !important; }
          .auth-mobile-logo { display: block !important; }
        }
      `}</style>
    </SiteWide>
  );
}

export default Signin;